<template>
  <div
    class="logo-credor"
    :data-cy="destaque ? 'destaqueCredorOriginario' : undefined"
  >
    <div
      v-if="identidade && destaque"
      class="logo-credor__marca"
      data-cy="credorOriginarioMarca"
    >
      <svg
        class="logo-credor__iniciais"
        viewBox="0 0 40 40"
        width="40"
        height="40"
        :role="identidade.nome ? undefined : 'img'"
        :aria-hidden="identidade.nome ? true : undefined"
      >
        <title v-if="!identidade.nome">{{ destaque.nome }}</title>
        <circle class="logo-credor__iniciais-fundo" cx="20" cy="20" r="20" />
        <text
          class="logo-credor__iniciais-texto"
          x="20"
          y="20"
          :font-size="identidade.tamanhoFonteIniciais"
        >{{ identidade.iniciais }}</text>
      </svg>
      <span
        v-if="identidade.nome"
        class="logo-credor__nome"
        :style="{ fontSize: `${identidade.tamanhoFonteNome}px` }"
      >{{ identidade.nome }}</span>
    </div>
    <span
      v-else-if="destaque"
      class="logo-credor__avatar"
      data-cy="credorOriginarioAvatar"
      aria-hidden="true"
    >
      {{ destaque.inicial }}
    </span>
    <img
      v-else
      :src="imageSrc"
      alt=""
    >
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { resolverIdentidadeCredorOriginario } from '@/components/LogoCredor/identidadeCredorOriginario';
import { logoModules } from '@/components/LogoCredor/logoCredorAssets';
import { normalizarSlugCredor } from '@/components/LogoCredor/normalizarSlugCredor';
import {
  type DividaParaDestaqueOriginario,
  resolverDestaqueCredorOriginario,
} from '@/components/LogoCredor/resolverDestaqueCredorOriginario';

const props = defineProps<{
  credor: string;
  divida?: DividaParaDestaqueOriginario | null;
  /**
   * Só o header do card de dívida reserva espaço para o nome do originário; nos demais pontos o
   * slot do logo é pequeno demais e o destaque continua sendo a inicial sozinha.
   */
  exibirNomeCredorOriginario?: boolean;
}>();

const logoMapCredores: Record<string, string> = Object.fromEntries(
  Object.entries(logoModules).map(([path, mod]) => [path.split('/').pop() ?? '', mod.default])
);

const sanitizedCredor = computed(() => normalizarSlugCredor(props.credor));

/** O originário aparece só como texto: nenhuma imagem de logo de terceiro é carregada (RN04). */
const destaque = computed(() => resolverDestaqueCredorOriginario(props.divida));

const identidade = computed(() => {
  if (!props.exibirNomeCredorOriginario || destaque.value === undefined) {
    return undefined;
  }

  return resolverIdentidadeCredorOriginario(destaque.value.nome);
});

const imageSrc = computed(() => logoMapCredores[`${sanitizedCredor.value}.png`] ?? '');
</script>

<style scoped lang="scss">
.logo-credor {
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: left;
  }
}

/** Um único grafite para todo credor originário: a marca de terceiro nunca traz cor própria. */
.logo-credor__marca {
  --grafite-credor-originario: var(--acerto-colors-neutral-800, #2b3033);

  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 152px;

  @include responsive($tablet) {
    max-width: 176px;
  }
}

.logo-credor__iniciais {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
}

.logo-credor__iniciais-fundo {
  fill: var(--grafite-credor-originario);
}

.logo-credor__iniciais-texto {
  fill: #fff;
  font-weight: 700;
  letter-spacing: -0.02em;
  text-anchor: middle;
  dominant-baseline: central;
}

/** O nome só quebra em espaço: `Itaú` partido em `Ita-ú` não é nome de credor nenhum. */
.logo-credor__nome {
  color: var(--grafite-credor-originario);
  font-weight: 700;
  line-height: 1.15;
  overflow-wrap: normal;
  word-break: normal;
}

.logo-credor__avatar {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 1.25rem;
  font-weight: 700;
  color: #fff;
  background-color: var(--acerto-colors-green-800, #1a4d2e);
  flex-shrink: 0;
}
</style>
